import * as React from 'react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

interface Option {
	value: string;
	icon?: React.ReactNode;
	className?: string;
}

interface SmallSlideSelectorProps {
	value: string;
	onChange: (value: string) => void;
	options: Option[];
	className?: string;
	activeColor?: string;
}

export function SmallSlideSelector({
	value,
	onChange,
	options,
	className,
	activeColor = 'purple',
}: SmallSlideSelectorProps) {
	const activeOption = options.find((opt) => opt.value === value);
	const activeIndex = options.findIndex((opt) => opt.value === value);

	return (
		<div
			className={cn(
				'relative rounded-full p-1.5 bg-neutral-800 dark:bg-neutral-900 border-2 border-neutral-700 dark:border-neutral-800',
				'shadow-md cursor-pointer',
				className
			)}
		>
			<div className='relative z-0 flex h-[55px]'>
				{options.map((option, index) => (
					<button
						key={option.value}
						onClick={() => onChange(option.value)}
						className='relative z-10 flex-1 cursor-pointer rounded-full focus:outline-none'
						aria-label={`Select ${option.value}`}
						style={{
							// Make the clickable area more obvious on hover
							borderRadius: index === 0 ? '9999px 0 0 9999px' : '0 9999px 9999px 0'
						}}
					/>
				))}

				<motion.div
					className={cn(
						'absolute inset-1 rounded-full flex items-center justify-center z-0 pointer-events-none',
						activeOption?.className || `bg-${activeColor}-600`
					)}
					initial={false}
					animate={{
						x: `${activeIndex * 84.5}%`,
					}}
					transition={{
						type: 'spring',
						stiffness: 200,
						damping: 25,
						mass: 1,
						restDelta: 0.001,
						restSpeed: 0.001,
						bounce: 0.1,
					}}
					style={{
						width: `${100 / options.length}%`,
					}}
				>
					<div className='flex items-center justify-center'>
						<span className='text-2xl text-foreground -translate-x-[1px] -translate-y-[1px]'>
							{activeOption?.icon}
						</span>
					</div>
				</motion.div>
			</div>
		</div>
	);
}
